import { useState, useEffect, useRef, useCallback } from 'react';

export interface LightboxPhoto {
  url: string;
  caption?: string;
  tag?: string;
  tagColor?: string;
  uploadedBy?: string;
  date?: string;
}

interface PhotoLightboxProps {
  photos: LightboxPhoto[];
  initialIndex: number;
  onClose: () => void;
}

export default function PhotoLightbox({ photos, initialIndex, onClose }: PhotoLightboxProps) {
  const [index, setIndex] = useState(initialIndex);
  const [scale, setScale] = useState(1);
  const [translateX, setTranslateX] = useState(0);
  const [translateY, setTranslateY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // Touch tracking refs
  const lastTouchRef = useRef<{ x: number; y: number } | null>(null);
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const pinchStartDistRef = useRef<number | null>(null);
  const pinchStartScaleRef = useRef<number>(1);
  const dragStartRef = useRef<{ x: number; y: number; tx: number; ty: number } | null>(null);
  const lastTapRef = useRef<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const photo = photos[index];

  // Reset zoom when changing photos
  useEffect(() => {
    setScale(1);
    setTranslateX(0);
    setTranslateY(0);
  }, [index]);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goTo(index - 1);
      if (e.key === 'ArrowRight') goTo(index + 1);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [index]);

  // Prevent body scroll while lightbox is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const goTo = useCallback((newIndex: number) => {
    if (newIndex < 0 || newIndex >= photos.length) return;
    setIndex(newIndex);
  }, [photos.length]);

  const getDistance = (t1: Touch, t2: Touch) => {
    const dx = t1.clientX - t2.clientX;
    const dy = t1.clientY - t2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      touchStartRef.current = { x: touch.clientX, y: touch.clientY, time: Date.now() };
      lastTouchRef.current = { x: touch.clientX, y: touch.clientY };
      if (scale > 1) {
        dragStartRef.current = { x: touch.clientX, y: touch.clientY, tx: translateX, ty: translateY };
        setIsDragging(true);
      }
    } else if (e.touches.length === 2) {
      pinchStartDistRef.current = getDistance(e.touches[0], e.touches[1]);
      pinchStartScaleRef.current = scale;
      touchStartRef.current = null;
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    e.preventDefault();
    if (e.touches.length === 2 && pinchStartDistRef.current !== null) {
      // Pinch zoom
      const dist = getDistance(e.touches[0], e.touches[1]);
      const newScale = Math.min(Math.max(pinchStartScaleRef.current * (dist / pinchStartDistRef.current), 1), 4);
      setScale(newScale);
      if (newScale <= 1) {
        setTranslateX(0);
        setTranslateY(0);
      }
    } else if (e.touches.length === 1 && scale > 1 && dragStartRef.current) {
      // Pan when zoomed
      const touch = e.touches[0];
      const dx = touch.clientX - dragStartRef.current.x;
      const dy = touch.clientY - dragStartRef.current.y;
      setTranslateX(dragStartRef.current.tx + dx);
      setTranslateY(dragStartRef.current.ty + dy);
    }
    if (e.touches.length === 1) {
      lastTouchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    pinchStartDistRef.current = null;
    setIsDragging(false);
    dragStartRef.current = null;

    if (e.changedTouches.length === 1 && touchStartRef.current) {
      const touch = e.changedTouches[0];
      const dx = touch.clientX - touchStartRef.current.x;
      const dy = touch.clientY - touchStartRef.current.y;
      const dt = Date.now() - touchStartRef.current.time;
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);

      // Double tap to zoom
      const now = Date.now();
      if (absDx < 10 && absDy < 10 && dt < 300) {
        if (now - lastTapRef.current < 350) {
          // Double tap
          if (scale > 1) {
            setScale(1);
            setTranslateX(0);
            setTranslateY(0);
          } else {
            setScale(2.5);
          }
          lastTapRef.current = 0;
          return;
        }
        lastTapRef.current = now;
      }

      // Swipe left/right to navigate (only when not zoomed)
      if (scale <= 1 && absDx > 50 && absDx > absDy * 1.5 && dt < 400) {
        if (dx < 0) goTo(index + 1);
        else goTo(index - 1);
        return;
      }

      // Swipe down to close (only when not zoomed)
      if (scale <= 1 && dy > 80 && absDy > absDx * 1.5 && dt < 400) {
        onClose();
        return;
      }
    }

    touchStartRef.current = null;
  };

  const handleBackdropClick = () => {
    if (scale <= 1 && !isDragging) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col bg-black"
      style={{ touchAction: 'none' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 flex-shrink-0 bg-black/80 backdrop-blur-sm">
        <div className="flex-1 min-w-0">
          {photo.caption && (
            <p className="text-white text-sm font-semibold truncate">{photo.caption}</p>
          )}
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            {photo.tag && (
              <span
                className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white"
                style={{ backgroundColor: photo.tagColor || '#374151' }}
              >
                {photo.tag}
              </span>
            )}
            {photo.uploadedBy && (
              <span className="text-white/50 text-xs">{photo.uploadedBy}</span>
            )}
            {photo.date && (
              <span className="text-white/40 text-xs">{photo.date}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0 ml-3">
          {photos.length > 1 && (
            <span className="text-white/50 text-xs font-medium">{index + 1} / {photos.length}</span>
          )}
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 text-white cursor-pointer active:bg-white/20 transition-colors"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
      </div>

      {/* Image area */}
      <div
        ref={containerRef}
        className="flex-1 flex items-center justify-center overflow-hidden relative"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleBackdropClick}
      >
        <img
          src={photo.url}
          alt={photo.caption || 'Photo'}
          className="max-w-full max-h-full object-contain select-none"
          onLoad={(e) => {
            const target = e.target as HTMLImageElement;
            const rect = target.getBoundingClientRect();
            // eslint-disable-next-line no-console
            console.log('[PhotoLightbox] image loaded:', {
              fileName: photo.caption || '',
              src: photo.url,
              naturalWidth: target.naturalWidth,
              naturalHeight: target.naturalHeight,
              renderedWidth: Math.round(rect.width),
              renderedHeight: Math.round(rect.height),
              ratio: target.naturalWidth / rect.width,
              type: 'original file_url',
            });
            if (target.naturalWidth < 800) {
              // eslint-disable-next-line no-console
              console.warn('[PhotoLightbox] naturalWidth < 800 — source file is low resolution');
            }
            if (target.naturalWidth <= rect.width) {
              // eslint-disable-next-line no-console
              console.warn('[PhotoLightbox] naturalWidth <= renderedWidth — image is being upscaled or source is low resolution');
            }
          }}
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            console.warn('[PhotoLightbox] lightbox image failed to load:', photo.url);
            target.style.display = 'none';
            const parent = target.parentElement;
            if (parent && !parent.querySelector('.lightbox-error')) {
              const errorDiv = document.createElement('div');
              errorDiv.className = 'lightbox-error text-white/50 text-sm flex items-center gap-2';
              errorDiv.innerHTML = '<i class="ri-error-warning-line"></i> Image could not be loaded';
              parent.appendChild(errorDiv);
            }
          }}
          style={{
            transform: `scale(${scale}) translate(${translateX / scale}px, ${translateY / scale}px)`,
            transition: isDragging ? 'none' : 'transform 0.2s ease',
            cursor: scale > 1 ? 'grab' : 'default',
            userSelect: 'none',
            WebkitUserSelect: 'none',
          }}
          draggable={false}
          onClick={(e) => e.stopPropagation()}
        />

        {/* Prev/Next arrows — desktop + mobile */}
        {photos.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); goTo(index - 1); }}
              disabled={index === 0}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-black/40 text-white cursor-pointer disabled:opacity-20 hover:bg-black/60 transition-colors active:scale-95"
            >
              <i className="ri-arrow-left-s-line text-2xl"></i>
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); goTo(index + 1); }}
              disabled={index === photos.length - 1}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-black/40 text-white cursor-pointer disabled:opacity-20 hover:bg-black/60 transition-colors active:scale-95"
            >
              <i className="ri-arrow-right-s-line text-2xl"></i>
            </button>
          </>
        )}

        {/* Zoom hint (shown briefly) */}
        {scale <= 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 pointer-events-none">
            <span className="text-white/30 text-xs">Pinch to zoom · Swipe to navigate</span>
          </div>
        )}
      </div>

      {/* Thumbnail strip (if multiple photos) */}
      {photos.length > 1 && (
        <div className="flex-shrink-0 bg-black/80 backdrop-blur-sm px-4 py-3">
          <div className="flex gap-2 overflow-x-auto scrollbar-hide justify-center">
            {photos.map((p, i) => (
              <div
                key={i}
                className="flex-shrink-0 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => goTo(i)}
                  className={`w-12 h-12 transition-all cursor-pointer ${
                    i === index ? 'ring-2 ring-white opacity-100' : 'opacity-40 hover:opacity-70'
                  }`}
                >
                  <img
                    src={p.url}
                    alt={p.caption || ''}
                    className="w-full h-full object-cover object-center"
                    loading="lazy"
                    decoding="async"
                  />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
